/**
 * Aterrizaje usuarios v2 — acciones clave + patrones perfil×retención.
 * RPC: get_early_landing_analytics(p_days)
 */

import { useQuery } from '@tanstack/react-query';
import { dbClient } from '@/services/db-client';
import { clampLandingAnalyticsDays } from '@/components/analytics/periodOptions';

export interface ActivatorStat {
    users: number;
    pct: number;
}

export type ActivatorsMap = Record<string, ActivatorStat>;

export interface SessionFunnelStep {
    session: number;
    users: number;
    pct_of_registered: number;
    pct_of_previous: number;
}

export interface DayRetentionStep {
    day: number;
    eligible: number;
    active: number;
    pct: number;
}

export interface ActivationBySession {
    session: number;
    users_reached: number;
    activators: ActivatorsMap;
}

export interface DayBehavior {
    day: number;
    eligible: number;
    active: number;
    pct_active: number;
    activators: ActivatorsMap;
}

export interface SegmentBlock {
    definition: string;
    users: number;
    early_activators: ActivatorsMap;
}

export interface LiftRow {
    key: string;
    label: string;
    retained_pct: number;
    churned_pct: number;
    lift: number;
}

export interface LandingInsight {
    severity: 'warning' | 'info';
    title: string;
    body: string;
}

export interface TopPageRow {
    page: string;
    users: number;
    views: number;
}

export interface KeyActionRow {
    key: string;
    label: string;
    category: string;
    users: number;
    pct: number;
}

export interface ProfileStepRow {
    step: string;
    label: string;
    completed: number;
    pct: number;
}

export interface ActivatorCatalogItem {
    key: string;
    label: string;
    category: string;
}

export interface ExperienceSegment {
    segment: string;
    users: number;
    session_2_pct: number;
    session_5_pct: number;
    session_10_pct: number;
    day_7_pct: number;
    retained_pct: number;
    creator_pct: number;
    lurker_pct: number;
    intel_pct: number;
    social_pct: number;
}

export interface WaterSegment {
    segment: string;
    users: number;
    session_2_pct: number;
    session_5_pct: number;
    day_7_pct: number;
    retained_pct: number;
    creator_pct: number;
}

export interface BehaviorSegment {
    segment: string;
    definition: string;
    users: number;
    session_5_pct: number;
    day_7_pct: number;
    retained_pct: number;
}

export interface ModalitySegment {
    modality: string;
    users: number;
    session_5_pct: number;
    day_7_pct: number;
    creator_pct: number;
}

export interface ActionRetentionRow {
    key: string;
    label: string;
    category: string;
    did_users: number;
    skip_users: number;
    did_session_5_pct: number;
    skip_session_5_pct: number;
    did_day_7_pct: number;
    skip_day_7_pct: number;
    lift_session_5: number;
    lift_day_7: number;
}

export interface ProfileActionWinner {
    profile_dim: string;
    profile_value: string;
    best_action: string;
    best_action_label: string;
    did_users: number;
    did_session_5_pct: number;
    skip_session_5_pct: number;
    lift: number;
    recommendation: string;
}

export interface PatternOpportunity {
    severity: 'warning' | 'info';
    title: string;
    body: string;
    audience: string;
    action_to_foster: string | null;
}

export interface MainScreenRow {
    screen_key: string;
    screen_label: string;
    profile: string;
    users_in_profile: number;
    visitors: number;
    visit_pct: number;
    visitor_session_5_pct: number;
    skip_session_5_pct: number;
    lift_session_5: number;
    visitor_day_7_pct: number;
    skip_day_7_pct: number;
    lift_day_7: number;
    value_signal: string;
}

export interface LandingPatterns {
    by_experience_level: ExperienceSegment[];
    by_water_type: WaterSegment[];
    by_behavior: BehaviorSegment[];
    by_modality: ModalitySegment[];
    action_retention_matrix: ActionRetentionRow[];
    profile_x_action_winners: ProfileActionWinner[];
    opportunities: PatternOpportunity[];
    by_main_screen: MainScreenRow[];
}

export interface EarlyLandingFilters {
    experience?: string | null;
    water?: string | null;
    modality?: string | null;
}

export interface EarlyLandingAnalytics {
    period_days: number;
    cohort: {
        registered: number;
        with_session: number;
        definition: string;
    };
    session_funnel: SessionFunnelStep[];
    day_retention: DayRetentionStep[];
    activation_by_session: ActivationBySession[];
    day_behavior: DayBehavior[];
    key_actions_48h: KeyActionRow[];
    profile_steps: ProfileStepRow[];
    activator_catalog: ActivatorCatalogItem[];
    retained_vs_churned: {
        retained: SegmentBlock | null;
        churned: SegmentBlock | null;
        early_window_label: string;
        lift: LiftRow[];
    };
    patterns: LandingPatterns;
    top_pages_sessions_1_3: TopPageRow[];
    summary_kpis: {
        registered: number;
        with_session: number;
        session_2_pct: number;
        session_5_pct: number;
        day_1_pct: number;
        day_7_pct: number;
        day_30_pct: number;
    };
    insights: LandingInsight[];
}

export const CATEGORY_LABELS: Record<string, string> = {
    activacion: 'Activación',
    perfil: 'Perfil',
    core: 'Core',
    social: 'Social',
    rincon: 'Rincón',
    contenido: 'Contenido',
    clubes: 'Clubes',
    gamificacion: 'Gamificación',
    intel: 'Intel / IA',
    tienda: 'Tienda',
    retenacion: 'Retención',
};

export const EXPERIENCE_LABELS: Record<string, string> = {
    principiante: 'Principiante',
    intermedio: 'Intermedio',
    experto: 'Experto',
    sin_dato: 'Sin dato',
};

export const WATER_LABELS: Record<string, string> = {
    dulce: 'Agua dulce',
    salada: 'Agua salada',
    ambas: 'Ambas',
    sin_dato: 'Sin dato',
};

export const BEHAVIOR_LABELS: Record<string, string> = {
    creators: 'Creators',
    lurkers: 'Lurkers',
    social: 'Social',
    intel_users: 'Intel',
    gamers: 'Gamers',
};

function asActivators(raw: unknown): ActivatorsMap {
    if (!raw || typeof raw !== 'object') return {};
    const obj = raw as Record<string, Partial<ActivatorStat>>;
    const out: ActivatorsMap = {};
    for (const [key, v] of Object.entries(obj)) {
        out[key] = {
            users: Number(v?.users ?? 0),
            pct: Number(v?.pct ?? 0),
        };
    }
    return out;
}

function asSegment(raw: unknown): SegmentBlock | null {
    if (!raw || typeof raw !== 'object') return null;
    const s = raw as Record<string, unknown>;
    return {
        definition: String(s.definition ?? ''),
        users: Number(s.users ?? 0),
        early_activators: asActivators(s.early_activators),
    };
}

function emptyPatterns(): LandingPatterns {
    return {
        by_experience_level: [],
        by_water_type: [],
        by_behavior: [],
        by_modality: [],
        action_retention_matrix: [],
        profile_x_action_winners: [],
        opportunities: [],
        by_main_screen: [],
    };
}

export function parseEarlyLandingResponse(raw: unknown): EarlyLandingAnalytics | null {
    if (raw == null) return null;

    let payload: unknown = raw;
    if (typeof payload === 'string') {
        try {
            payload = JSON.parse(payload);
        } catch {
            return null;
        }
    }

    if (Array.isArray(payload)) {
        payload = payload[0];
    }

    if (payload && typeof payload === 'object') {
        const obj = payload as Record<string, unknown>;
        const wrapped = obj.get_early_landing_analytics;
        if (wrapped && typeof wrapped === 'object') {
            payload = wrapped;
        }
    }

    if (!payload || typeof payload !== 'object') return null;
    const p = payload as Record<string, unknown>;
    const periodDays = Number(p.period_days);
    if (!Number.isFinite(periodDays)) return null;

    const rvc = (p.retained_vs_churned && typeof p.retained_vs_churned === 'object'
        ? p.retained_vs_churned
        : {}) as Record<string, unknown>;

    const summary = (p.summary_kpis && typeof p.summary_kpis === 'object'
        ? p.summary_kpis
        : {}) as Record<string, number>;

    const cohort = (p.cohort && typeof p.cohort === 'object' ? p.cohort : {}) as Record<string, unknown>;
    const patternsRaw = (p.patterns && typeof p.patterns === 'object' ? p.patterns : {}) as Record<string, unknown>;

    return {
        period_days: periodDays,
        cohort: {
            registered: Number(cohort.registered ?? 0),
            with_session: Number(cohort.with_session ?? 0),
            definition: String(cohort.definition ?? ''),
        },
        session_funnel: Array.isArray(p.session_funnel) ? (p.session_funnel as SessionFunnelStep[]) : [],
        day_retention: Array.isArray(p.day_retention) ? (p.day_retention as DayRetentionStep[]) : [],
        activation_by_session: Array.isArray(p.activation_by_session)
            ? (p.activation_by_session as ActivationBySession[]).map((row) => ({
                ...row,
                activators: asActivators(row.activators),
            }))
            : [],
        day_behavior: Array.isArray(p.day_behavior)
            ? (p.day_behavior as DayBehavior[]).map((row) => ({
                ...row,
                activators: asActivators(row.activators),
            }))
            : [],
        key_actions_48h: Array.isArray(p.key_actions_48h) ? (p.key_actions_48h as KeyActionRow[]) : [],
        profile_steps: Array.isArray(p.profile_steps) ? (p.profile_steps as ProfileStepRow[]) : [],
        activator_catalog: Array.isArray(p.activator_catalog) ? (p.activator_catalog as ActivatorCatalogItem[]) : [],
        retained_vs_churned: {
            retained: asSegment(rvc.retained),
            churned: asSegment(rvc.churned),
            early_window_label: String(rvc.early_window_label ?? 'Primeras 48 h'),
            lift: Array.isArray(rvc.lift) ? (rvc.lift as LiftRow[]) : [],
        },
        patterns: {
            ...emptyPatterns(),
            by_experience_level: Array.isArray(patternsRaw.by_experience_level)
                ? (patternsRaw.by_experience_level as ExperienceSegment[])
                : [],
            by_water_type: Array.isArray(patternsRaw.by_water_type)
                ? (patternsRaw.by_water_type as WaterSegment[])
                : [],
            by_behavior: Array.isArray(patternsRaw.by_behavior)
                ? (patternsRaw.by_behavior as BehaviorSegment[])
                : [],
            by_modality: Array.isArray(patternsRaw.by_modality)
                ? (patternsRaw.by_modality as ModalitySegment[])
                : [],
            action_retention_matrix: Array.isArray(patternsRaw.action_retention_matrix)
                ? (patternsRaw.action_retention_matrix as ActionRetentionRow[])
                : [],
            profile_x_action_winners: Array.isArray(patternsRaw.profile_x_action_winners)
                ? (patternsRaw.profile_x_action_winners as ProfileActionWinner[])
                : [],
            opportunities: Array.isArray(patternsRaw.opportunities)
                ? (patternsRaw.opportunities as PatternOpportunity[])
                : [],
            by_main_screen: Array.isArray(patternsRaw.by_main_screen)
                ? (patternsRaw.by_main_screen as MainScreenRow[])
                : [],
        },
        top_pages_sessions_1_3: Array.isArray(p.top_pages_sessions_1_3)
            ? (p.top_pages_sessions_1_3 as TopPageRow[])
            : [],
        summary_kpis: {
            registered: Number(summary.registered ?? 0),
            with_session: Number(summary.with_session ?? 0),
            session_2_pct: Number(summary.session_2_pct ?? 0),
            session_5_pct: Number(summary.session_5_pct ?? 0),
            day_1_pct: Number(summary.day_1_pct ?? 0),
            day_7_pct: Number(summary.day_7_pct ?? 0),
            day_30_pct: Number(summary.day_30_pct ?? 0),
        },
        insights: Array.isArray(p.insights) ? (p.insights as LandingInsight[]) : [],
    };
}

function normFilter(value: string | null | undefined): string | null {
    if (!value) return null;
    const t = value.trim();
    if (!t || t.toLowerCase() === 'all') return null;
    return t;
}

export function useEarlyLandingAnalytics(days: number = 7, filters: EarlyLandingFilters = {}) {
    const experience = normFilter(filters.experience);
    const water = normFilter(filters.water);
    const modality = normFilter(filters.modality);
    const safeDays = clampLandingAnalyticsDays(days);

    return useQuery<EarlyLandingAnalytics>({
        queryKey: ['admin', 'early-landing', safeDays, experience, water, modality],
        queryFn: async () => {
            const { data, error } = await dbClient.rpc('get_early_landing_analytics', {
                p_days: safeDays,
                p_experience_level: experience,
                p_water_type: water,
                p_modality: modality,
            });
            if (error) {
                const msg = error.message || '';
                if (/timeout|timed out|statement timeout|canceling statement/i.test(msg)) {
                    throw new Error(
                        `La consulta de Aterrizaje tardó demasiado (${safeDays} días). Prueba con 7 días o menos.`
                    );
                }
                throw error;
            }
            const parsed = parseEarlyLandingResponse(data);
            if (!parsed) {
                throw new Error('Respuesta inválida de get_early_landing_analytics');
            }
            return parsed;
        },
        retry: 0,
        staleTime: 5 * 60_000,
        refetchOnWindowFocus: false,
    });
}

/** Etiqueta legible para un activador (catálogo RPC o fallback). */
export function activatorLabel(
    key: string,
    catalog: ActivatorCatalogItem[] | undefined
): string {
    const hit = catalog?.find((c) => c.key === key);
    return hit?.label ?? key;
}
